import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { MainNav } from '../components/ui/MainNav';
import { FeatureDetailModal } from '../components/ui/FeatureDetailModal';
import { AktionenPopup } from '../components/AktionenPopup';
import { AktionenSection } from '../components/AktionenSection';
import { fmsFeatures } from '../data/featureDetails';
import { 
  MapPin, Activity, Wrench, Calendar, TrendingUp, 
  DollarSign, Bell, Award, Shield, Zap, Database,
  Truck, Users, BarChart3, Clock, CheckCircle, ArrowRight,
  Smartphone, AlertTriangle, Target, TrendingDown, Car
} from 'lucide-react';

export const FMSLandingPage = () => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [selectedFeature, setSelectedFeature] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      id: 'fleet-management',
      icon: Truck,
      title: 'Intelligentes Buchungssystem',
      subtitle: 'Fahrzeugpool effizient verwalten',
      description: 'Mitarbeiter können Firmenfahrzeuge direkt über die Webanwendung buchen. Das System zeigt Verfügbarkeiten in Echtzeit, verhindert Doppelbuchungen und optimiert die Auslastung Ihrer Flotte automatisch.',
      image: 'https://images.unsplash.com/photo-1696530744771-cd08b25d5508',
      details: [
        'Echtzeit-Verfügbarkeitsanzeige aller Fahrzeuge',
        'Automatische Benachrichtigungen bei Buchungsbestätigung',
        'Konfliktfreie Planung durch intelligente Algorithmen',
        'Responsive Webanwendung für Buchung von unterwegs',
        'Integration mit Kalender-Systemen'
      ]
    },
    {
      id: 'obd-diagnostics',
      icon: Activity,
      title: 'OBD-Fehlerspeicher Fernauslesung',
      subtitle: 'Diagnose ohne Werkstattbesuch',
      description: 'Lesen Sie Fehlercodes und Diagnosedaten Ihrer Fahrzeuge in Echtzeit aus – egal wo sich das Fahrzeug befindet. Teltonika FMM003 OBD-Dongles senden kontinuierlich Daten über Motor, Getriebe und alle elektronischen Systeme.',
      image: 'https://images.unsplash.com/photo-1662079043272-5feaaadf63a6',
      details: [
        'Live-Auslese aller OBD2-Fehlercodes (P0, P1, P2, P3)',
        'Detaillierte Fehler-Beschreibungen auf Deutsch',
        'Historischer Verlauf aller Störungen',
        'Automatische Benachrichtigung bei kritischen Fehlern',
        'Prognose: Wann muss das Fahrzeug zur Werkstatt?'
      ]
    },
    {
      id: 'gps-tracking',
      icon: MapPin,
      title: 'Live GPS-Tracking',
      subtitle: 'Flottenübersicht in Echtzeit',
      description: 'Sehen Sie alle Fahrzeuge auf einer Karte in Echtzeit. Verfolgen Sie Routen, analysieren Sie gefahrene Strecken und optimieren Sie Tourenplanung. Geo-Fencing warnt Sie, wenn Fahrzeuge definierte Bereiche verlassen.',
      image: 'https://images.unsplash.com/photo-1761590542271-27a3518ab957',
      details: [
        'Echtzeit-Positionierung mit 10-Sekunden-Updates',
        'Geo-Fencing: Automatische Warnungen bei Zonenüberschreitungen',
        'Routen-Historie: Wo war welches Fahrzeug wann?',
        'Optimierte Tourenplanung basierend auf Verkehrsdaten',
        'Integration mit Google Maps / OpenStreetMap'
      ]
    },
    {
      id: 'driver-management',
      icon: TrendingUp,
      title: 'Fahrer-Effizienz-Score',
      subtitle: 'Fahrverhalten analysieren & verbessern',
      description: 'Unser KI-System bewertet das Fahrverhalten jedes Fahrers anhand von über 20 Parametern: Beschleunigung, Bremsverhalten, Geschwindigkeit, Leerlaufzeiten und mehr. Gamification motiviert zu spritsparendem Fahren.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71',
      details: [
        'Score-Bewertung: 0-100 Punkte basierend auf Fahrstil',
        'Analyse: Hartes Bremsen, aggressive Beschleunigung, Kurvenverhalten',
        'Kraftstoff-Effizienz: Wie viel Sprit wird durch schlechten Fahrstil verschwendet?',
        'Fahrer-Ranking: Wer fährt am effizientesten?',
        'Individuelles Coaching: Verbesserungsvorschläge für jeden Fahrer'
      ]
    },
    {
      id: 'fleet-management',
      icon: Calendar,
      title: 'Automatische Wartungsplanung',
      subtitle: 'Keine verpassten Service-Termine mehr',
      description: 'Das System erkennt automatisch, wann Wartungen fällig sind – basierend auf Kilometerstand, Betriebsstunden und Herstellervorgaben. Sie erhalten Erinnerungen und können Werkstatttermine direkt über die Plattform buchen.',
      image: 'https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86',
      details: [
        'KI-gestützte Vorhersage: Wann ist die nächste Inspektion fällig?',
        'Automatische Erinnerungen 2 Wochen vor Fälligkeit',
        'Integration mit Werkstatt-Systemen für Terminbuchung',
        'Wartungs-Historie: Alle durchgeführten Services dokumentiert',
        'Kostenkontrolle: Budget-Planung für kommende Wartungen'
      ]
    },
    {
      id: 'analytics',
      icon: BarChart3,
      title: 'Umfassendes Reporting',
      subtitle: 'Datenbasierte Entscheidungen treffen',
      description: 'Visualisieren Sie alle wichtigen KPIs Ihrer Flotte: Gesamtkosten, Kilometerleistung, Auslastung, Kraftstoffverbrauch und mehr. Exportieren Sie Reports als PDF oder Excel für Ihre Buchhaltung.',
      image: 'https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b',
      details: [
        'Dashboard: Alle wichtigen Kennzahlen auf einen Blick',
        'Kosten-Analyse: Was kostet jedes Fahrzeug pro km?',
        'Auslastungs-Report: Welche Fahrzeuge stehen oft ungenutzt?',
        'Kraftstoff-Tracking: Verbrauch vs. Budget',
        'Export: PDF, Excel, CSV für Weiterverarbeitung'
      ]
    }
  ];

  const benefits = [
    { icon: DollarSign, title: 'Bis zu 30% Kosteneinsparung', desc: 'Durch optimierte Wartung, effizientere Routen und reduzierten Kraftstoffverbrauch' },
    { icon: Clock, title: '5 Stunden/Woche einsparen', desc: 'Automatisierung von Verwaltungsaufgaben, Berichten und Terminplanung' },
    { icon: TrendingDown, title: '25% weniger Kraftstoffverbrauch', desc: 'Durch Fahrer-Coaching und optimierte Routenplanung' },
    { icon: Shield, title: '90% weniger Ausfallzeiten', desc: 'Predictive Maintenance erkennt Probleme, bevor sie kritisch werden' },
    { icon: CheckCircle, title: '100% Compliance', desc: 'Automatische Dokumentation erfüllt alle gesetzlichen Anforderungen' },
    { icon: Award, title: 'ROI in 6 Monaten', desc: 'Die Investition amortisiert sich durch Einsparungen in unter einem halben Jahr' }
  ];

  const plans = [
    {
      name: 'FMS Basic',
      price: '49',
      period: 'Monat',
      features: [
        'Bis zu 5 Fahrzeuge',
        'Bis zu 5 Fahrer',
        'GPS-Tracking',
        'Basis-Diagnose',
        'E-Mail Support'
      ],
      highlighted: false
    },
    {
      name: 'FMS Pro',
      price: '99',
      period: 'Monat',
      features: [
        'Bis zu 25 Fahrzeuge',
        'Bis zu 25 Fahrer',
        'GPS-Tracking + Routen',
        'Vollständige OBD-Diagnose',
        'Wartungsplanung',
        'Fahrzeugpool',
        'Prioritäts-Support'
      ],
      highlighted: true
    },
    {
      name: 'FMS Enterprise',
      price: 'Individuell',
      period: '',
      features: [
        'Unbegrenzte Fahrzeuge',
        'Unbegrenzte Fahrer',
        'Alle Features',
        'KI-Analysen',
        'API-Zugang',
        'Dedizierter Account Manager',
        '24/7 Premium Support'
      ],
      highlighted: false
    }
  ];

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: '#0B0F14' }}>
      <MainNav />
      
      {/* Hero Section */}
      <section className="relative py-32 px-4 md:px-8 mt-20 overflow-hidden">
        <motion.div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1733662095268-7a88cb5fa10e)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `translateY(${scrollY * 0.5}px) scale(${1 + scrollY * 0.0003})`,
            transition: 'transform 0.1s ease-out'
          }}
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 10, ease: "easeOut" }}
        />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ backgroundColor: '#1EA0E620', border: '1px solid #1EA0E6' }}
            >
              <Truck className="w-4 h-4" style={{ color: '#7CD4FD' }} />
              <span className="text-sm font-semibold" style={{ color: '#7CD4FD' }}>
                Professionelles Flottenmanagement
              </span>
            </div>
            
            <h1 
              className="text-5xl md:text-7xl font-bold mb-6" 
              style={{ color: '#E6EDF3', fontFamily: 'Manrope', lineHeight: '1.2' }}
            >
              Fuhrpark-Management für{' '}
              <span 
                style={{ 
                  background: 'linear-gradient(135deg, #7CD4FD 0%, #1EA0E6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                moderne Unternehmen
              </span>
            </h1>
            
            <p 
              className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto" 
              style={{ color: '#94A3B8' }}
            >
              GPS-Tracking, OBD-Diagnose, Wartungsplanung, Fahrzeugpool – alles in einem System. 
              Sparen Sie bis zu 30% Kosten und 5 Stunden pro Woche.
            </p>
            
            <div className="flex gap-4 justify-center flex-wrap">
              <Button
                size="lg"
                onClick={() => navigate('/signup')}
                className="group"
                style={{ 
                  backgroundColor: '#1EA0E6', 
                  color: '#fff', 
                  fontSize: '1.1rem', 
                  padding: '1.5rem 3rem',
                  boxShadow: '0 10px 40px rgba(30, 160, 230, 0.3)'
                }}
              >
                Jetzt kostenlos testen
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                style={{ 
                  borderColor: '#1F2937', 
                  color: '#E6EDF3', 
                  fontSize: '1.1rem', 
                  padding: '1.5rem 3rem',
                  backgroundColor: '#0B0F1480',
                  backdropFilter: 'blur(10px)'
                }}
              >
                Mehr erfahren
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 md:px-8" style={{ backgroundColor: '#0F141A' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
            Messbare <span style={{ color: '#7CD4FD' }}>Ergebnisse</span>
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => (
              <Card 
                key={idx}
                className="hover:scale-105 transition-all duration-300"
                style={{ 
                  backgroundColor: '#0B0F14', 
                  borderColor: '#1F2937'
                }}
              >
                <CardContent className="p-8">
                  <benefit.icon className="w-12 h-12 mb-4" style={{ color: '#1EA0E6' }} />
                  <h3 className="text-2xl font-bold mb-3" style={{ color: '#E6EDF3' }}>{benefit.title}</h3>
                  <p style={{ color: '#94A3B8' }}>{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Showcases */}
      <section id="features" className="py-32 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-20" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
            Alle <span style={{ color: '#7CD4FD' }}>Funktionen</span> im Detail
          </h2>
          
          {features.map((feature, idx) => (
            <div 
              key={idx}
              className={`grid md:grid-cols-2 gap-12 items-center mb-32 ${idx % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
            >
              <div className={idx % 2 === 1 ? 'md:order-2' : ''}>
                <div className="mb-4">
                  <feature.icon className="w-12 h-12 mb-4" style={{ color: '#1EA0E6' }} />
                </div>
                <h3 className="text-3xl md:text-4xl font-bold mb-2" style={{ color: '#E6EDF3' }}>
                  {feature.title}
                </h3>
                <p className="text-xl mb-4" style={{ color: '#7CD4FD' }}>
                  {feature.subtitle}
                </p>
                <p className="text-lg mb-8" style={{ color: '#94A3B8' }}>
                  {feature.description}
                </p>
                
                <div className="space-y-3 mb-6">
                  {feature.details.map((detail, dIdx) => (
                    <div key={dIdx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#22C55E' }} />
                      <span style={{ color: '#E6EDF3' }}>{detail}</span>
                    </div>
                  ))}
                </div>
                
                <button
                  onClick={() => setSelectedFeature(feature.id)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-all duration-300 hover:scale-105"
                  style={{ 
                    backgroundColor: '#1EA0E6',
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <span className="font-semibold">Mehr erfahren</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
              
              <div className={idx % 2 === 1 ? 'md:order-1' : ''}>
                <div 
                  className="rounded-2xl overflow-hidden"
                  style={{ 
                    border: '2px solid #1F2937',
                    boxShadow: '0 20px 60px rgba(124, 212, 253, 0.1)'
                  }}
                >
                  <img 
                    src={feature.image}
                    alt={feature.title}
                    className="w-full h-96 object-cover"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow - Wichtigste Funktionen im Alltag */}
      <section className="py-32 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-8" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
            Workflow: Die wichtigsten Funktionen <span style={{ color: '#7CD4FD' }}>im Alltag</span>
          </h2>
          <p className="text-xl text-center mb-20" style={{ color: '#94A3B8' }}>
            So nutzen Sie das FMS in typischen Szenarien
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Workflow 1: Fahrzeug-Buchung */}
            <Card 
              onClick={() => setSelectedFeature('fleet-management')}
              className="hover:scale-105 transition-all duration-300 cursor-pointer"
              style={{ 
                backgroundColor: '#0F141A', 
                borderColor: '#1F2937',
                borderLeft: '4px solid #1EA0E6'
              }}
            >
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: '#1EA0E620' }}
                  >
                    <Truck className="w-6 h-6" style={{ color: '#1EA0E6' }} />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: '#E6EDF3' }}>
                    Fahrzeug buchen
                  </h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                    >
                      1
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      Mitarbeiter öffnet Webanwendung und wählt "Fahrzeug buchen"
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                    >
                      2
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      System zeigt verfügbare Fahrzeuge in Echtzeit
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                    >
                      3
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      Zeitraum wählen & buchen - Bestätigung per E-Mail
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#22C55E', color: '#fff' }}
                    >
                      ✓
                    </span>
                    <p className="text-sm font-semibold" style={{ color: '#22C55E' }}>
                      Fahrzeug ist reserviert, keine Doppelbuchungen möglich
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Workflow 2: Pannenfall */}
            <Card 
              onClick={() => setSelectedFeature('obd-diagnostics')}
              className="hover:scale-105 transition-all duration-300 cursor-pointer"
              style={{ 
                backgroundColor: '#0F141A', 
                borderColor: '#1F2937',
                borderLeft: '4px solid #EF4444'
              }}
            >
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: '#EF444420' }}
                  >
                    <AlertTriangle className="w-6 h-6" style={{ color: '#EF4444' }} />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: '#E6EDF3' }}>
                    Pannenfall Live-Support
                  </h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#EF4444', color: '#fff' }}
                    >
                      1
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      OBD-Dongle erkennt kritischen Fehler automatisch
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#EF4444', color: '#fff' }}
                    >
                      2
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      Flottenmanager erhält sofort Push-Benachrichtigung
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#EF4444', color: '#fff' }}
                    >
                      3
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      Exakte GPS-Position & Fehlercode werden übermittelt
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#22C55E', color: '#fff' }}
                    >
                      ✓
                    </span>
                    <p className="text-sm font-semibold" style={{ color: '#22C55E' }}>
                      Schnelle Hilfe durch nächste Werkstatt organisieren
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Workflow 3: Wartungsplanung */}
            <Card 
              onClick={() => setSelectedFeature('fleet-management')}
              className="hover:scale-105 transition-all duration-300 cursor-pointer"
              style={{ 
                backgroundColor: '#0F141A', 
                borderColor: '#1F2937',
                borderLeft: '4px solid #F59E0B'
              }}
            >
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: '#F59E0B20' }}
                  >
                    <Calendar className="w-6 h-6" style={{ color: '#F59E0B' }} />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: '#E6EDF3' }}>
                    Automatische Wartung
                  </h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#F59E0B', color: '#fff' }}
                    >
                      1
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      KI analysiert Kilometerstand & Betriebsstunden
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#F59E0B', color: '#fff' }}
                    >
                      2
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      2 Wochen vor Fälligkeit: Automatische Erinnerung
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#F59E0B', color: '#fff' }}
                    >
                      3
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      Termin direkt über das System bei Partnerwerkstatt buchen
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#22C55E', color: '#fff' }}
                    >
                      ✓
                    </span>
                    <p className="text-sm font-semibold" style={{ color: '#22C55E' }}>
                      Keine verpassten Services, längere Fahrzeuglebensdauer
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Workflow 4: Fahrer-Coaching */}
            <Card 
              onClick={() => setSelectedFeature('driver-management')}
              className="hover:scale-105 transition-all duration-300 cursor-pointer"
              style={{ 
                backgroundColor: '#0F141A', 
                borderColor: '#1F2937',
                borderLeft: '4px solid #7CD4FD'
              }}
            >
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: '#7CD4FD20' }}
                  >
                    <TrendingUp className="w-6 h-6" style={{ color: '#7CD4FD' }} />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: '#E6EDF3' }}>
                    Fahrer-Effizienz steigern
                  </h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#7CD4FD', color: '#fff' }}
                    >
                      1
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      System erfasst Fahrverhalten in Echtzeit
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#7CD4FD', color: '#fff' }}
                    >
                      2
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      Wöchentlicher Score-Report für jeden Fahrer
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#7CD4FD', color: '#fff' }}
                    >
                      3
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      Individuelle Verbesserungstipps & Coaching
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#22C55E', color: '#fff' }}
                    >
                      ✓
                    </span>
                    <p className="text-sm font-semibold" style={{ color: '#22C55E' }}>
                      25% weniger Kraftstoffverbrauch & weniger Verschleiß
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Workflow 5: Kosten-Tracking */}
            <Card 
              onClick={() => setSelectedFeature('analytics')}
              className="hover:scale-105 transition-all duration-300 cursor-pointer"
              style={{ 
                backgroundColor: '#0F141A', 
                borderColor: '#1F2937',
                borderLeft: '4px solid #22C55E'
              }}
            >
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: '#22C55E20' }}
                  >
                    <DollarSign className="w-6 h-6" style={{ color: '#22C55E' }} />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: '#E6EDF3' }}>
                    Kosten im Blick behalten
                  </h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#22C55E', color: '#fff' }}
                    >
                      1
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      Alle Kosten automatisch erfassen (Kraftstoff, Wartung, etc.)
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#22C55E', color: '#fff' }}
                    >
                      2
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      Dashboard zeigt Kosten pro Fahrzeug & pro Kilometer
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#22C55E', color: '#fff' }}
                    >
                      3
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      Monatliche Reports für Buchhaltung exportieren
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#22C55E', color: '#fff' }}
                    >
                      ✓
                    </span>
                    <p className="text-sm font-semibold" style={{ color: '#22C55E' }}>
                      Volle Kostentransparenz & Budget-Kontrolle
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Workflow 6: Route optimieren */}
            <Card 
              onClick={() => setSelectedFeature('gps-tracking')}
              className="hover:scale-105 transition-all duration-300 cursor-pointer"
              style={{ 
                backgroundColor: '#0F141A', 
                borderColor: '#1F2937',
                borderLeft: '4px solid #A78BFA'
              }}
            >
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: '#A78BFA20' }}
                  >
                    <MapPin className="w-6 h-6" style={{ color: '#A78BFA' }} />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: '#E6EDF3' }}>
                    Route optimieren
                  </h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#A78BFA', color: '#fff' }}
                    >
                      1
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      GPS-Tracking zeigt alle Fahrzeuge auf Karte
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#A78BFA', color: '#fff' }}
                    >
                      2
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      KI analysiert gefahrene Routen & identifiziert Potenzial
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#A78BFA', color: '#fff' }}
                    >
                      3
                    </span>
                    <p className="text-sm" style={{ color: '#94A3B8' }}>
                      System schlägt effizientere Routen vor
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <span 
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#22C55E', color: '#fff' }}
                    >
                      ✓
                    </span>
                    <p className="text-sm font-semibold" style={{ color: '#22C55E' }}>
                      Kürzere Fahrzeiten, weniger Kraftstoff, mehr Aufträge
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-32 px-4 md:px-8" style={{ backgroundColor: '#0F141A' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-20" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
            So einfach geht's
          </h2>

          <div className="space-y-16">
            {[
              { step: '01', title: 'OBD-Dongles installieren', desc: 'Teltonika FMM003 Dongles einfach in die OBD2-Buchse Ihrer Fahrzeuge stecken' },
              { step: '02', title: 'Fahrzeuge & Fahrer anlegen', desc: 'In 5 Minuten alle Fahrzeuge und Fahrer im System hinterlegen' },
              { step: '03', title: 'Live-Tracking starten', desc: 'Sofort Echtzeit-Daten: GPS-Position, OBD-Werte, Fahrverhalten' },
              { step: '04', title: 'Kosten sparen', desc: 'Optimierte Wartung, effizientere Routen, besseres Fahrverhalten = 30% Ersparnis' }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-8">
                <div 
                  className="text-6xl font-bold flex-shrink-0"
                  style={{ 
                    color: '#1F2937',
                    WebkitTextStroke: '2px #7CD4FD'
                  }}
                >
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: '#E6EDF3' }}>{item.title}</h3>
                  <p className="text-lg" style={{ color: '#94A3B8' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-32 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Manrope', color: '#E6EDF3' }}>
              Transparente <span style={{ color: '#7CD4FD' }}>Preise</span>
            </h2>
            <p className="text-xl" style={{ color: '#94A3B8' }}>
              Wähle den Plan, der zu deiner Flottengröße passt
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, idx) => (
              <Card 
                key={idx}
                className="relative hover:scale-105 transition-all duration-300"
                style={{
                  backgroundColor: '#0F141A',
                  borderColor: idx === 1 ? '#1EA0E6' : '#1F2937',
                  borderWidth: idx === 1 ? '2px' : '1px'
                }}
              >
                {idx === 1 && (
                  <div 
                    className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-sm font-bold"
                    style={{ backgroundColor: '#1EA0E6', color: '#fff' }}
                  >
                    BELIEBT
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-2" style={{ color: '#E6EDF3' }}>{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-5xl font-bold" style={{ color: '#7CD4FD' }}>
                      {plan.price === 'Individuell' ? plan.price : `€${plan.price}`}
                    </span>
                    {plan.period && <span style={{ color: '#94A3B8' }}>/{plan.period}</span>}
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, fIdx) => (
                      <li key={fIdx} className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                        <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#22C55E' }} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full"
                    onClick={() => navigate('/signup')}
                    style={{
                      backgroundColor: idx === 1 ? '#1EA0E6' : '#1F2937',
                      color: '#fff'
                    }}
                  >
                    {plan.price === 'Individuell' ? 'Kontakt aufnehmen' : 'Jetzt starten'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-4 md:px-8" style={{ backgroundColor: '#0F141A' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
            Bereit, Ihre Flotte zu optimieren?
          </h2>
          <p className="text-xl mb-12" style={{ color: '#94A3B8' }}>
            Starten Sie jetzt kostenlos und sehen Sie innerhalb von 24 Stunden erste Ergebnisse
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/signup')}
            className="group w-full md:w-auto"
            style={{ 
              backgroundColor: '#1EA0E6', 
              color: '#fff', 
              fontSize: '1.1rem', 
              padding: '1.5rem 2rem',
              boxShadow: '0 10px 40px rgba(30, 160, 230, 0.3)'
            }}
          >
            <span className="hidden md:inline">Kostenlos testen (keine Kreditkarte erforderlich)</span>
            <span className="md:hidden">Kostenlos testen</span>
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>

      {/* Dynamic Aktionen Section */}
      <AktionenSection pageType="fms" />

      {/* Footer */}
      <footer className="relative py-12 px-4 md:px-8 border-t" style={{ borderColor: '#1F2937' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Company */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Truck className="w-8 h-8" style={{ color: '#1EA0E6' }} />
                <span className="text-xl font-bold" style={{ color: '#E6EDF3' }}>CarCortex FMS</span>
              </div>
              <p className="text-sm" style={{ color: '#94A3B8' }}>
                Professionelles Flottenmanagement für moderne Unternehmen
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="font-semibold mb-4" style={{ color: '#E6EDF3' }}>Produkt</h4>
              <ul className="space-y-2 text-sm" style={{ color: '#94A3B8' }}>
                <li><a href="#features" className="hover:text-blue-400 transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-blue-400 transition-colors">Preise</a></li>
                <li><a href="/" className="hover:text-blue-400 transition-colors">Diagnose-System</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-semibold mb-4" style={{ color: '#E6EDF3' }}>Support</h4>
              <ul className="space-y-2 text-sm" style={{ color: '#94A3B8' }}>
                <li><a href="/contact" className="hover:text-blue-400 transition-colors">Kontakt</a></li>
                <li><a href="#" className="hover:text-blue-400 transition-colors">Hilfe-Center</a></li>
                <li><a href="#" className="hover:text-blue-400 transition-colors">API-Dokumentation</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4" style={{ color: '#E6EDF3' }}>Rechtliches</h4>
              <ul className="space-y-2 text-sm" style={{ color: '#94A3B8' }}>
                <li><a href="/datenschutz" className="hover:text-blue-400 transition-colors">Datenschutz</a></li>
                <li><a href="/agb" className="hover:text-blue-400 transition-colors">AGB</a></li>
                <li><a href="/impressum" className="hover:text-blue-400 transition-colors">Impressum</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t flex flex-col md:flex-row items-center justify-between gap-4" style={{ borderColor: '#1F2937' }}>
            <p className="text-sm" style={{ color: '#94A3B8' }}>
              © 2025 CarCortex-AI. Alle Rechte vorbehalten.
            </p>
            <div className="flex items-center gap-6">
              <Shield className="w-5 h-5" style={{ color: '#374151' }} />
              <CheckCircle className="w-5 h-5" style={{ color: '#374151' }} />
              <Award className="w-5 h-5" style={{ color: '#374151' }} />
            </div>
          </div>
        </div>
      </footer>
      
      {/* Feature Detail Modal */}
      <FeatureDetailModal 
        isOpen={selectedFeature !== null}
        onClose={() => setSelectedFeature(null)}
        feature={selectedFeature ? fmsFeatures[selectedFeature] : null}
      />

      {/* Aktionen Popup */}
      <AktionenPopup pageType="fms" />
    </div>
  );
};
